import { useMemo, useState } from "react";
import { Grid, Num, Pick, Result, SHEETS, UNIT_MM, card, convertVals, fmt, sheetMM } from "./ui";

// Best layout of w×h items (gutter g) inside an area AW×AH, allowing one orientation switch (mixed layout).
export function impose(AW, AH, w, h, g) {
  const fit = (len, s) => (s > len ? 0 : Math.floor((len + g) / (s + g)));
  const grid = (x0, y0, W, H, iw, ih, rot) => {
    const nx = fit(W, iw), ny = fit(H, ih), out = [];
    for (let i = 0; i < nx; i++) for (let j = 0; j < ny; j++) out.push({ x: x0 + i * (iw + g), y: y0 + j * (ih + g), w: iw, h: ih, rot });
    return out;
  };
  let best = [];
  const tryLayout = (cells) => { if (cells.length > best.length) best = cells; };
  tryLayout(grid(0, 0, AW, AH, w, h, false));
  tryLayout(grid(0, 0, AW, AH, h, w, true));
  for (let rows = 1; rows * (h + g) <= AH + g; rows++) {
    const used = rows * (h + g);
    tryLayout([...grid(0, 0, AW, used - g, w, h, false), ...grid(0, used, AW, AH - used, h, w, true)]);
  }
  for (let rows = 1; rows * (w + g) <= AH + g; rows++) {
    const used = rows * (w + g);
    tryLayout([...grid(0, 0, AW, used - g, h, w, true), ...grid(0, used, AW, AH - used, w, h, false)]);
  }
  for (let cols = 1; cols * (w + g) <= AW + g; cols++) {
    const used = cols * (w + g);
    tryLayout([...grid(0, 0, used - g, AH, w, h, false), ...grid(used, 0, AW - used, AH, h, w, true)]);
  }
  return best;
}

function Layout({ SW, SH, m, cells, bleed }) {
  const sw = Math.max(SW, SH) / 300;
  return (
    <svg data-testid="imposition-layout" viewBox={`-5 -5 ${SW + 10} ${SH + 10}`} style={{ width: "100%", maxHeight: 420, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12 }}>
      <rect x={0} y={0} width={SW} height={SH} fill="#fff" stroke="#0F172A" strokeWidth={sw}/>
      <rect x={m.left} y={m.top} width={SW - m.left - m.right} height={SH - m.top - m.bottom} fill="none" stroke="#94A3B8" strokeDasharray={`${sw * 4} ${sw * 3}`} strokeWidth={sw}/>
      <rect x={0} y={0} width={SW} height={m.top} fill="#FDE68A" opacity={0.5}/>
      {cells.map((c, i) => (
        <g key={i}>
          <rect x={m.left + c.x} y={m.top + c.y} width={c.w} height={c.h} fill={c.rot ? "#DBEAFE" : "#DCFCE7"} stroke={c.rot ? "#2563EB" : "#16A34A"} strokeWidth={sw}/>
          {bleed > 0 && <rect x={m.left + c.x + bleed} y={m.top + c.y + bleed} width={c.w - bleed * 2} height={c.h - bleed * 2} fill="none" stroke="#475569" strokeWidth={sw / 2}/>}
        </g>
      ))}
    </svg>
  );
}

export function ImpositionTool() {
  const last = JSON.parse(localStorage.getItem("gpp_last_dieline") || "null");
  const [unit, setUnit] = useState("mm");
  const [sheet, setSheet] = useState("23x36in");
  const [s, setS] = useState({ SW: 584.2, SH: 914.4, IW: 90, IH: 55, bleed: 3, gutter: 3, grip: 12, side: 5, tail: 5, qty: 10000, spoil: 5, price: 0 });
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const k = UNIT_MM[unit];
  const preset = sheetMM(sheet);
  const SW = preset ? preset[0] : s.SW * k, SH = preset ? preset[1] : s.SH * k;
  const iw = (Number(s.IW) || 0) * k + 2 * (Number(s.bleed) || 0) * k, ih = (Number(s.IH) || 0) * k + 2 * (Number(s.bleed) || 0) * k;
  const m = { top: (Number(s.grip) || 0) * k, bottom: (Number(s.tail) || 0) * k, left: (Number(s.side) || 0) * k, right: (Number(s.side) || 0) * k };
  const AW = SW - m.left - m.right, AH = SH - m.top - m.bottom;
  const cells = useMemo(() => (iw > 0 && ih > 0 && AW > 0 && AH > 0 ? impose(AW, AH, iw, ih, (Number(s.gutter) || 0) * k) : []), [AW, AH, iw, ih, s.gutter, k]);
  const ups = cells.length;
  const net = ups ? Math.ceil((Number(s.qty) || 0) / ups) : 0;
  const gross = Math.ceil(net * (1 + (Number(s.spoil) || 0) / 100));
  const util = ups ? (ups * ((Number(s.IW) || 0) * k) * ((Number(s.IH) || 0) * k)) / (SW * SH) * 100 : 0;
  const cost = gross * (Number(s.price) || 0);
  const useDieline = () => { setUnit("mm"); setS({ ...s, IW: Math.round(last.w * 10) / 10, IH: Math.round(last.h * 10) / 10, bleed: 0 }); };
  return (
    <div data-testid="tool-imposition" style={{ display: "grid", gap: 16 }}>
      <div style={card}>
        <Grid>
          <Pick testid="imp-unit" label="Units" value={unit} onChange={(u) => { setS(convertVals(s, ["SW", "SH", "IW", "IH", "bleed", "gutter", "grip", "side", "tail"], unit, u)); setUnit(u); }} options={[["mm", "mm"], ["cm", "cm"], ["in", "inches"]]}/>
          <Pick testid="imp-sheet" label="Press sheet" value={sheet} onChange={setSheet} options={SHEETS}/>
          {!preset && <Num testid="imp-sheet-w" label="Sheet width" suffix={unit} value={s.SW} onChange={set("SW")}/>}
          {!preset && <Num testid="imp-sheet-h" label="Sheet height" suffix={unit} value={s.SH} onChange={set("SH")}/>}
          <Num testid="imp-item-w" label="Item / blank width" suffix={unit} value={s.IW} onChange={set("IW")}/>
          <Num testid="imp-item-h" label="Item / blank height" suffix={unit} value={s.IH} onChange={set("IH")}/>
          <Num testid="imp-bleed" label="Bleed each side" suffix={unit} value={s.bleed} onChange={set("bleed")}/>
          <Num testid="imp-gutter" label="Gutter" suffix={unit} value={s.gutter} onChange={set("gutter")}/>
          <Num testid="imp-grip" label="Gripper margin" suffix={unit} value={s.grip} onChange={set("grip")}/>
          <Num testid="imp-side" label="Side margins" suffix={unit} value={s.side} onChange={set("side")}/>
          <Num testid="imp-tail" label="Tail margin" suffix={unit} value={s.tail} onChange={set("tail")}/>
          <Num testid="imp-qty" label="Order quantity" value={s.qty} onChange={set("qty")} step={1}/>
          <Num testid="imp-spoil" label="Spoilage" suffix="%" value={s.spoil} onChange={set("spoil")}/>
          <Num testid="imp-price" label="Price per sheet" suffix="₹, optional" value={s.price} onChange={set("price")}/>
        </Grid>
        {last && <button data-testid="imp-use-dieline" className="so-btn" style={{ marginTop: 12 }} onClick={useDieline}>Use my last dieline ({fmt(last.w, 1)} × {fmt(last.h, 1)} mm)</button>}
      </div>
      <Grid min={150}>
        <Result testid="imp-ups" big label="Ups per sheet" value={ups}/>
        <Result testid="imp-sheets" big label="Sheets incl. spoilage" value={fmt(gross, 0)}/>
        <Result testid="imp-net" label="Net sheets" value={fmt(net, 0)}/>
        <Result testid="imp-util" label="Sheet utilisation" value={`${fmt(util, 1)}%`}/>
        <Result testid="imp-sheet-size" label="Sheet" value={`${fmt(SW, 0)} × ${fmt(SH, 0)} mm`}/>
        {cost > 0 && <Result testid="imp-cost" label="Paper cost · per unit" value={`₹${fmt(cost, 0)} · ₹${fmt(cost / (Number(s.qty) || 1), 3)}`}/>}
      </Grid>
      <div style={card}>
        <b style={{ fontSize: 13 }}>Sheet layout</b>
        <span style={{ fontSize: 11.5, color: "#64748b" }}> · yellow = gripper · green = normal · blue = rotated</span>
        <div style={{ marginTop: 10 }}>{ups ? <Layout SW={SW} SH={SH} m={m} cells={cells} bleed={(Number(s.bleed) || 0) * k}/> : <p data-testid="imp-no-fit" style={{ color: "#B91C1C" }}>The item does not fit on this sheet with these margins.</p>}</div>
      </div>
    </div>
  );
}
